import React, { useState } from "react";
import { motion } from "framer-motion";
import { Download, Loader2, CheckCircle2, FileText, Users, Zap, Star } from "lucide-react";
import { toast } from "sonner";
import PublicNav from "@/components/landing/PublicNav";
import Footer from "@/components/landing/Footer";
import { useSeo } from "@/hooks/useSeo";
import { API_BASE } from "@/lib/api";

const BENEFITS = [
    { icon: FileText, title: "8 techniques testées sur le terrain", desc: "Séquences email, multi-canal, personnalisation efficace — pas de théorie." },
    { icon: Zap, title: "Workflow 60 jours pour signer 3 clients", desc: "Semaine par semaine, ce qu'il faut faire et dans quel ordre." },
    { icon: Users, title: "Adapté aux PME EU / DOM-COM", desc: "Contrairement aux guides US, celui-ci parle du marché francophone." },
];

const TESTIMONIALS_QUOTES = [
    { q: "Enfin un playbook qui parle de la Guyane et pas juste de la Silicon Valley.", a: "SDR indépendant" },
    { q: "J'ai signé 2 clients en 5 semaines en appliquant la séquence 3 étapes.", a: "Founder d'une agence B2B" },
];

export default function Playbook() {
    const [email, setEmail] = useState("");
    const [firstName, setFirstName] = useState("");
    const [state, setState] = useState("idle"); // idle | submitting | done | error
    const [errorMsg, setErrorMsg] = useState("");

    useSeo({
        title: "Playbook gratuit — Prospection B2B Guyane 2026 · BOXEB2B",
        description: "Télécharge le playbook gratuit : 8 techniques testées pour signer tes 3 premiers clients B2B en Guyane / EU en 60 jours. Zéro théorie, pas de spam.",
        keywords: "playbook prospection B2B, guide gratuit Guyane, cold email francophone, séquence outbound PDF",
        canonical: typeof window !== "undefined" ? `${window.location.origin}/playbook` : "",
    });

    async function submit(e) {
        e.preventDefault();
        if (!email.trim() || state === "submitting") return;
        setState("submitting");
        setErrorMsg("");
        try {
            const res = await fetch(`${API_BASE}/playbook/download`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: email.trim().toLowerCase(), first_name: firstName.trim() }),
            });
            if (!res.ok) throw new Error("Download failed");
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "boxeb2b-playbook-prospection-guyane.pdf";
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            setState("done");
            toast.success("Playbook téléchargé — vérifie tes téléchargements");
        } catch (err) {
            setErrorMsg("Le téléchargement a échoué, réessaie.");
            setState("error");
        }
    }

    return (
        <div className="relative min-h-screen bg-brand-void text-white">
            <PublicNav />

            <section className="pt-32 pb-16 px-6 lg:px-10">
                <div className="mx-auto max-w-6xl grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
                    {/* Left: Copy */}
                    <div className="lg:col-span-3">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-cyan/10 border border-brand-cyan/30 text-brand-cyan text-xs font-mono mb-5" data-testid="playbook-badge">
                            <Star size={12} /> PLAYBOOK GRATUIT · PDF · 20 min de lecture
                        </div>
                        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05]">
                            Le playbook qui signe tes <span className="text-brand-cyan">3 premiers clients</span> en 60 jours.
                        </h1>
                        <p className="mt-5 text-lg text-zinc-400 leading-relaxed max-w-2xl">
                            8 techniques concrètes de prospection B2B en français.
                            Écrites par des opérationnels, testées sur le terrain en Guyane et en métropole.
                            Aucune promesse magique, aucun jargon marketing.
                        </p>

                        <ul className="mt-8 space-y-4 max-w-xl">
                            {BENEFITS.map(({ icon: Icon, title, desc }) => (
                                <li key={title} className="flex items-start gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-brand-indigo/20 border border-brand-indigo/40 flex items-center justify-center shrink-0">
                                        <Icon size={14} className="text-brand-cyan" />
                                    </div>
                                    <div>
                                        <div className="font-semibold text-white text-sm">{title}</div>
                                        <div className="text-sm text-zinc-400 mt-0.5">{desc}</div>
                                    </div>
                                </li>
                            ))}
                        </ul>

                        <div className="mt-8 flex flex-wrap gap-2">
                            {TESTIMONIALS_QUOTES.map((t, i) => (
                                <div key={i} className="rounded-xl p-3.5 bg-brand-surface border border-white/8 max-w-xs">
                                    <div className="text-sm text-zinc-300 italic leading-relaxed">« {t.q} »</div>
                                    <div className="text-[11px] text-zinc-500 font-mono mt-1.5">— {t.a}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right: Download form */}
                    <div className="lg:col-span-2 lg:sticky lg:top-24">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="rounded-2xl p-6 bg-gradient-to-br from-brand-indigo/25 via-brand-violet/15 to-brand-cyan/10 border border-brand-indigo/40"
                            data-testid="playbook-form-card"
                        >
                            {state === "done" ? (
                                <div className="text-center py-4">
                                    <CheckCircle2 size={48} className="text-emerald-400 mx-auto mb-3" />
                                    <div className="font-display font-black text-xl tracking-tight text-white">
                                        Téléchargement démarré
                                    </div>
                                    <p className="mt-2 text-sm text-zinc-300">
                                        Ton PDF est dans tes téléchargements. Ouvre-le et lis-le dans le train demain.
                                    </p>
                                    <p className="mt-4 text-xs text-brand-cyan font-mono">
                                        Bonus : code <b>PLAYBOOK50</b> = 50 crédits offerts à l'inscription
                                    </p>
                                    <a
                                        href="/register?ref=PLAYBOOK50"
                                        data-testid="playbook-cta-signup"
                                        className="mt-4 inline-flex btn-cta text-white font-semibold text-sm px-5 py-2.5 rounded-full"
                                    >
                                        Créer mon compte gratuit →
                                    </a>
                                </div>
                            ) : (
                                <>
                                    <div className="text-xs uppercase tracking-[0.22em] font-bold text-brand-cyan mb-2">100% gratuit</div>
                                    <div className="font-display font-black text-2xl tracking-tighter text-white">
                                        Envoie-moi le playbook
                                    </div>
                                    <p className="mt-1 text-xs text-zinc-400">Un email pour recevoir le PDF + 1 article utile par semaine. Zéro spam.</p>

                                    <form onSubmit={submit} className="mt-5 space-y-3">
                                        <input
                                            type="text"
                                            value={firstName}
                                            onChange={(e) => setFirstName(e.target.value)}
                                            placeholder="Ton prénom (optionnel)"
                                            disabled={state === "submitting"}
                                            data-testid="playbook-input-firstname"
                                            className="w-full px-4 py-2.5 rounded-full bg-brand-void border border-white/10 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-brand-cyan disabled:opacity-60"
                                        />
                                        <input
                                            type="email"
                                            required
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="ton@email.com"
                                            disabled={state === "submitting"}
                                            data-testid="playbook-input-email"
                                            className="w-full px-4 py-2.5 rounded-full bg-brand-void border border-white/10 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-brand-cyan disabled:opacity-60"
                                        />
                                        <button
                                            type="submit"
                                            disabled={state === "submitting" || !email.trim()}
                                            data-testid="playbook-submit"
                                            className="w-full btn-cta text-white font-semibold text-base px-6 py-3 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
                                        >
                                            {state === "submitting" ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                                            {state === "submitting" ? "Génération…" : "Télécharger le playbook"}
                                        </button>
                                    </form>
                                    {state === "error" && <div className="mt-2 text-xs text-rose-300">{errorMsg}</div>}
                                    <div className="mt-4 text-[10px] text-zinc-500 font-mono text-center">
                                        RGPD-clean · Unsubscribe en 1 clic · Pas de revente
                                    </div>
                                </>
                            )}
                        </motion.div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
